import { useState, useEffect } from 'react';
import { onboardingAPI, settingsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { MultiSelect } from '../../components/ui/multi-select';
import { Loader2, Check, X, Eye, Link as LinkIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';
import ModuleDisabled from '../../components/shared/ModuleDisabled';

const OnboardingManagement = () => {
    const { user } = useAuth();
    const { dialogProps, confirm } = useConfirmation();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [remarks, setRemarks] = useState('');
    const [processing, setProcessing] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({});
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [locations, setLocations] = useState([]);

    useEffect(() => {
        fetchRequests();
        fetchSettings();
    }, []);

    const fetchRequests = async () => {
        try {
            const response = await onboardingAPI.getRequests();
            setRequests(response.data);
        } catch (error) {
            if (error.response?.status === 403) {
                setError(error.response.data.detail);
            } else {
                toast.error('Failed to fetch onboarding requests');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const [deptRes, desgRes, locRes] = await Promise.all([
                settingsAPI.getDepartments(),
                settingsAPI.getDesignations(),
                settingsAPI.getLocations()
            ]);
            setDepartments(deptRes.data);
            setDesignations(desgRes.data);
            setLocations(locRes.data);
        } catch (error) {
            console.error('Failed to fetch settings:', error);
        }
    };

    const handleApprove = async (id) => {
        if (!formData.employee_code) {
            toast.error('Please assign an Employee Code before approval');
            return;
        }

        const confirmed = await confirm({
            title: 'Approve Onboarding',
            description: 'Are you sure you want to approve this onboarding request? This will create an active employee record.',
            confirmText: 'Approve',
        });
        if (!confirmed) return;

        setProcessing(true);
        try {
            await onboardingAPI.approve(id, remarks, formData);
            toast.success('Onboarding request approved');
            setIsViewOpen(false);
            fetchRequests();
        } catch (error) {
            toast.error('Failed to approve request');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async (id) => {
        if (!remarks) {
            toast.error('Please provide rejection remarks');
            return;
        }

        const confirmed = await confirm({
            title: 'Reject Onboarding',
            description: 'Are you sure you want to reject this onboarding request?',
            confirmText: 'Reject',
            variant: 'destructive',
        });
        if (!confirmed) return;

        setProcessing(true);
        try {
            await onboardingAPI.reject(id, remarks);
            toast.success('Onboarding request rejected');
            setIsViewOpen(false);
            fetchRequests();
        } catch (error) {
            toast.error('Failed to reject request');
        } finally {
            setProcessing(false);
        }
    };

    const copyOnboardingLink = () => {
        const baseUrl = window.location.origin;
        // User requested to remove /pagahr from link
        const link = `${baseUrl}/onboard?companyId=${user.company_id}`;
        navigator.clipboard.writeText(link);
        toast.success('Onboarding link copied to clipboard');
    };

    const getStatusBadge = (status) => {
        const styles = {
            PENDING: 'bg-yellow-100 text-yellow-800',
            APPROVED: 'bg-green-100 text-green-800',
            REJECTED: 'bg-red-100 text-red-800',
        };
        return <Badge className={styles[status]}>{status}</Badge>;
    };

    if (error) {
        return <ModuleDisabled moduleName="onboarding" />;
    }

    return (
        <div className="space-y-6 animate-slide-up">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Onboarding Requests</h1>
                    <p className="text-muted-foreground">Manage and approve new employee onboarding submissions</p>
                </div>
                <Button onClick={copyOnboardingLink} variant="outline">
                    <LinkIcon className="mr-2 h-4 w-4" />
                    Copy Onboarding Link
                </Button>
            </div>

            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Candidate Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Phone</TableHead>
                                <TableHead>Submitted At</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-10">
                                        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                                    </TableCell>
                                </TableRow>
                            ) : requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                                        No onboarding requests found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                requests.map((request) => (
                                    <TableRow key={request.id}>
                                        <TableCell className="font-medium">
                                            {request.first_name} {request.last_name}
                                        </TableCell>
                                        <TableCell>{request.email || request.personal_email || '-'}</TableCell>
                                        <TableCell>{request.phone || '-'}</TableCell>
                                        <TableCell>{new Date(request.submitted_at).toLocaleDateString()}</TableCell>
                                        <TableCell>{getStatusBadge(request.status)}</TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedRequest(request);
                                                    setFormData(request);
                                                    setRemarks(request.admin_remarks || '');
                                                    setEditMode(request.status === 'PENDING');
                                                    setIsViewOpen(true);
                                                }}
                                            >
                                                <Eye className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Onboarding Details</DialogTitle>
                        <DialogDescription>
                            Review and update the information submitted by the candidate
                        </DialogDescription>
                    </DialogHeader>

                    {formData && (
                        <div className="space-y-6 py-4">
                            <div className="flex justify-center mb-4">
                                {formData.profile_picture ? (
                                    <img 
                                        src={`${process.env.REACT_APP_BACKEND_URL}${formData.profile_picture}`} 
                                        alt="Profile" 
                                        className="h-24 w-24 rounded-full object-cover border-4 border-muted"
                                    />
                                ) : (
                                    <div className="h-24 w-24 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                                        No Photo
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <Label>First Name</Label>
                                    <Input
                                        value={formData.first_name || ''}
                                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                        disabled={!editMode}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label>Last Name</Label>
                                    <Input
                                        value={formData.last_name || ''}
                                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                        disabled={!editMode}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label>Email</Label>
                                    <Input
                                        value={formData.email || ''}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        disabled={!editMode}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label>Phone</Label>
                                    <Input
                                        value={formData.phone || ''}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        disabled={!editMode}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label>Personal Email</Label>
                                    <Input
                                        value={formData.personal_email || ''}
                                        onChange={(e) => setFormData({ ...formData, personal_email: e.target.value })}
                                        disabled={!editMode}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label>Date of Birth</Label>
                                    <Input
                                        type="date"
                                        value={formData.date_of_birth || ''}
                                        onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                                        disabled={!editMode}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label>Gender</Label>
                                    <Select
                                        value={formData.gender || ''}
                                        onValueChange={(value) => setFormData({ ...formData, gender: value })}
                                        disabled={!editMode}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select gender" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Male">Male</SelectItem>
                                            <SelectItem value="Female">Female</SelectItem>
                                            <SelectItem value="Other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <Label>Blood Group</Label>
                                    <Select
                                        value={formData.blood_group || ''}
                                        onValueChange={(value) => setFormData({ ...formData, blood_group: value })}
                                        disabled={!editMode}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Blood Group" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="A+">A+</SelectItem>
                                            <SelectItem value="A-">A-</SelectItem>
                                            <SelectItem value="B+">B+</SelectItem>
                                            <SelectItem value="B-">B-</SelectItem>
                                            <SelectItem value="AB+">AB+</SelectItem>
                                            <SelectItem value="AB-">AB-</SelectItem>
                                            <SelectItem value="O+">O+</SelectItem>
                                            <SelectItem value="O-">O-</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <Label>Date of Joining</Label>
                                    <Input
                                        type="date"
                                        value={formData.date_of_joining || ''}
                                        onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })}
                                        disabled={!editMode}
                                    />
                                </div>
                                    <div className="space-y-1">
                                        <Label>Employee Code *</Label>
                                        <Input
                                            placeholder="EMP-101"
                                            value={formData.employee_code || ''}
                                            onChange={(e) => setFormData({ ...formData, employee_code: e.target.value })}
                                            disabled={!editMode}
                                            className={!formData.employee_code && editMode ? 'border-red-500' : ''}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <Label className="text-sm font-semibold border-b block pb-1">Emergency Contact</Label>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-1">
                                            <Label className="text-xs">Name</Label>
                                            <Input
                                                value={formData.emergency_contact?.name || ''}
                                                onChange={(e) => setFormData({ ...formData, emergency_contact: { ...formData.emergency_contact, name: e.target.value } })}
                                                disabled={!editMode}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Relationship</Label>
                                            <Select
                                                value={formData.emergency_contact?.relationship || ''}
                                                onValueChange={(value) => setFormData({ ...formData, emergency_contact: { ...formData.emergency_contact, relationship: value } })}
                                                disabled={!editMode}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="Spouse">Spouse</SelectItem>
                                                    <SelectItem value="Parent">Parent</SelectItem>
                                                    <SelectItem value="Sibling">Sibling</SelectItem>
                                                    <SelectItem value="Friend">Friend</SelectItem>
                                                    <SelectItem value="Other">Other</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Phone</Label>
                                            <Input
                                                value={formData.emergency_contact?.phone || ''}
                                                onChange={(e) => setFormData({ ...formData, emergency_contact: { ...formData.emergency_contact, phone: e.target.value } })}
                                                disabled={!editMode}
                                            />
                                        </div>
                                    </div>
                                </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <Label>Department</Label>
                                    <Select
                                        value={formData.department || ''}
                                        onValueChange={(value) => setFormData({ ...formData, department: value })}
                                        disabled={!editMode}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select department" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {departments.map((dept) => (
                                                <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <Label>Designation</Label>
                                    <Select
                                        value={formData.designation || ''}
                                        onValueChange={(value) => setFormData({ ...formData, designation: value })}
                                        disabled={!editMode}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select designation" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {designations
                                                .filter(d => !formData.department || d.department_name === formData.department)
                                                .map((desg) => (
                                                    <SelectItem key={desg.id} value={desg.name}>{desg.name}</SelectItem>
                                                ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <Label>Address</Label>
                                <Input
                                    value={formData.address || ''}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    disabled={!editMode}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Work Locations</Label>
                                <MultiSelect
                                    options={locations.map(loc => ({ label: loc.name, value: loc.name }))}
                                    selected={formData.work_locations || []}
                                    onChange={(values) => setFormData({ ...formData, work_locations: values })}
                                    placeholder="Select locations..."
                                    disabled={!editMode}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <Label>Aadhaar</Label>
                                    <Input
                                        value={formData.aadhaar || ''}
                                        onChange={(e) => setFormData({ ...formData, aadhaar: e.target.value })}
                                        disabled={!editMode}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label>PAN</Label>
                                    <Input
                                        value={formData.pan || ''}
                                        onChange={(e) => setFormData({ ...formData, pan: e.target.value })}
                                        disabled={!editMode}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label>PF Account Number</Label>
                                    <Input
                                        value={formData.pf_account_number || ''}
                                        onChange={(e) => setFormData({ ...formData, pf_account_number: e.target.value })}
                                        disabled={!editMode}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label>UAN</Label>
                                    <Input
                                        value={formData.uan || ''}
                                        onChange={(e) => setFormData({ ...formData, uan: e.target.value })}
                                        disabled={!editMode}
                                    />
                                </div>
                            </div>

                            {formData.bank_details && (
                                <div className="bg-muted p-4 rounded-lg space-y-4">
                                    <h4 className="font-semibold text-sm">Bank Details</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1 col-span-2">
                                            <Label className="text-xs">Account Holder Name</Label>
                                            <Input
                                                value={formData.bank_details.account_name || ''}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    bank_details: { ...formData.bank_details, account_name: e.target.value }
                                                })}
                                                disabled={!editMode}
                                                className="bg-background"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Bank Name</Label>
                                            <Input
                                                value={formData.bank_details.bank_name || ''}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    bank_details: { ...formData.bank_details, bank_name: e.target.value }
                                                })}
                                                disabled={!editMode}
                                                className="bg-background"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Account Number</Label>
                                            <Input
                                                value={formData.bank_details.account_number || ''}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    bank_details: { ...formData.bank_details, account_number: e.target.value }
                                                })}
                                                disabled={!editMode}
                                                className="bg-background"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">IFSC Code</Label>
                                            <Input
                                                value={formData.bank_details.ifsc_code || ''}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    bank_details: { ...formData.bank_details, ifsc_code: e.target.value }
                                                })}
                                                disabled={!editMode}
                                                className="bg-background"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Branch</Label>
                                            <Input
                                                value={formData.bank_details.branch || ''}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    bank_details: { ...formData.bank_details, branch: e.target.value }
                                                })}
                                                disabled={!editMode}
                                                className="bg-background"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="remarks">Admin Remarks / Rejection Reason</Label>
                                <Textarea
                                    id="remarks"
                                    placeholder="Enter remarks here..."
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    disabled={!editMode || processing}
                                />
                            </div>

                            {editMode && (
                                <div className="flex gap-2 justify-end">
                                    <Button
                                        variant="destructive"
                                        onClick={() => handleReject(formData.id)}
                                        disabled={processing}
                                    >
                                        {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <X className="h-4 w-4 mr-2" />}
                                        Reject
                                    </Button>
                                    <Button
                                        onClick={() => handleApprove(formData.id)}
                                        disabled={processing}
                                    >
                                        {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                                        Approve & Onboard
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmationDialog {...dialogProps} />
        </div>
    );
};

export default OnboardingManagement;
